import React, { useState, useEffect, useRef } from "react";
import {
  getWidgetIcon,
  getDefaultDeviceIcon,
  getWidgetSubtitle,
} from "../widget-icon-registry";
import { Typography } from "@mhome/ui";

const Title1Widget = ({ widget, onTitleClick, onTitleHold, bindWidget }) => {
  const [title, setTitle] = useState(widget.title);
  const [subtitle, setSubtitle] = useState(widget.subtitle);
  const [icon, setIcon] = useState(null);

  const touchStateRef = useRef({
    startPos: { x: 0, y: 0 },
    startTime: 0,
    hasScrolled: false,
    longPressTriggered: false,
    pressTimer: null,
  });
  const mousePressTimerRef = useRef(null);
  const mousePressStartTimeRef = useRef(null);
  const mouseLongPressTriggeredRef = useRef(false);

  useEffect(() => {
    setTitle(widget.title);
    setSubtitle(widget.subtitle);
  }, [widget.title, widget.subtitle]);

  // Update icon and subtitle based on bind widget pattern
  useEffect(() => {
    if (bindWidget && bindWidget.pattern) {
      // console.log("bindWidget", bindWidget);
      const widgetIcon = getWidgetIcon(bindWidget.pattern, bindWidget);
      setIcon(widgetIcon || getDefaultDeviceIcon());

      // Get subtitle from helper
      const helperSubtitle = getWidgetSubtitle(bindWidget.pattern, bindWidget);
      if (helperSubtitle) {
        setSubtitle(helperSubtitle);
      } else if (widget.subtitle) {
        setSubtitle(widget.subtitle);
      }
    } else {
      setIcon(null);
      setSubtitle(widget.subtitle);
    }
  }, [bindWidget, widget.subtitle]);

  // Touch handlers for mobile
  const handleTouchStart = (event) => {
    if (!event.touches || !event.touches[0] || !onTitleHold) return;

    const touch = event.touches[0];
    const touchState = touchStateRef.current;
    const targetElement = event.currentTarget;

    // Reset touch state
    touchState.startPos = { x: touch.clientX, y: touch.clientY };
    touchState.startTime = Date.now();
    touchState.hasScrolled = false;
    touchState.longPressTriggered = false;

    // Clear any existing timer
    if (touchState.pressTimer) {
      clearTimeout(touchState.pressTimer);
    }

    // Start long press timer
    touchState.pressTimer = setTimeout(() => {
      if (!touchState.hasScrolled && !touchState.longPressTriggered) {
        touchState.longPressTriggered = true;
        onTitleHold(targetElement);
        // Haptic feedback if available
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
      }
    }, 800);
  };

  const handleTouchMove = (event) => {
    if (!event.touches || !event.touches[0]) return;

    const touch = event.touches[0];
    const touchState = touchStateRef.current;

    if (touchState.hasScrolled) return;

    const deltaX = Math.abs(touch.clientX - touchState.startPos.x);
    const deltaY = Math.abs(touch.clientY - touchState.startPos.y);

    if (deltaX > 10 || deltaY > 10) {
      touchState.hasScrolled = true;

      if (touchState.pressTimer) {
        clearTimeout(touchState.pressTimer);
        touchState.pressTimer = null;
      }
    }
  };

  const handleTouchEnd = (event) => {
    const touchState = touchStateRef.current;

    if (touchState.pressTimer) {
      clearTimeout(touchState.pressTimer);
      touchState.pressTimer = null;
    }

    if (touchState.hasScrolled) {
      event.preventDefault();
      event.stopPropagation();
    }

    setTimeout(() => {
      touchState.hasScrolled = false;
      touchState.longPressTriggered = false;
    }, 100);
  };

  // Mouse handlers for desktop
  const handleMouseDown = (event) => {
    if (!onTitleHold) return;
    const targetElement = event.currentTarget;
    mouseLongPressTriggeredRef.current = false;
    mousePressTimerRef.current = setTimeout(() => {
      mouseLongPressTriggeredRef.current = true;
      onTitleHold(targetElement);
    }, 800);
  };

  const handleMouseUp = () => {
    if (mousePressTimerRef.current) {
      clearTimeout(mousePressTimerRef.current);
      mousePressTimerRef.current = null;
    }
    // Reset the flag after a short delay to allow click handler to check it
    if (mouseLongPressTriggeredRef.current) {
      setTimeout(() => {
        mouseLongPressTriggeredRef.current = false;
      }, 100);
    }
  };

  const handleClick = (event) => {
    // Prevent click if we just finished scrolling
    if (touchStateRef.current.hasScrolled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Prevent click if long press was triggered (touch)
    if (touchStateRef.current.longPressTriggered) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Prevent click if long press was triggered (mouse)
    if (mouseLongPressTriggeredRef.current) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Call the onTitleClick callback if provided
    if (onTitleClick) {
      onTitleClick();
    }
  };

  return (
    <>
      <div
        className="card-title-trigger w-full h-full flex flex-row items-center cursor-pointer select-none gap-1"
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          WebkitTapHighlightColor: "transparent",
        }}
      >
        {/* Icon on the left */}
        {icon && (
          <div
            className="flex items-center justify-top shrink-0"
            style={{ height: "28px", width: "28px" }}
          >
            {icon}
          </div>
        )}

        {/* Title and subtitle stacked vertically on the right */}
        <div className="flex flex-col justify-center flex-1 min-w-0">
          {/* Title */}
          <Typography
            className="overflow-hidden text-ellipsis whitespace-nowrap"
            style={{
              fontSize: "12px",
              fontWeight: 600,
              lineHeight: "15px",
            }}
          >
            {title}
          </Typography>

          {/* Subtitle */}
          {subtitle && (
            <Typography
              className="overflow-hidden text-ellipsis whitespace-nowrap opacity-70"
              style={{
                fontSize: "10px",
                fontWeight: 400,
                lineHeight: "13px",
                color: "inherit",
              }}
            >
              {subtitle}
            </Typography>
          )}
        </div>
      </div>
    </>
  );
};

export default Title1Widget;
